import React from 'react';

import { type IntlShape, useIntl } from 'react-intl';

import AvatarGroup, { type AvatarGroupProps } from '@atlaskit/avatar-group/avatar-group';
import { cssMap } from '@atlaskit/css';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { useReportingLinesStore } from '@atlassian/teams';

import { messages } from './messages';
import * as Styled from './styles';
import { AccountIdentifierType, type ReportingLinesUser } from './types';

export interface AvatarData {
	name: string;
	label: string;
	isDisabled?: boolean;
	src?: string | undefined;
	href?: string | undefined;
}

const styles = cssMap({
	avatarGroupContainer: {
		marginBottom: token('space.100'),
	},
});

const appendMissingProfileStackItemData = (
	avatarData: AvatarData[],
	numMissingProfiles: number,
	intl: IntlShape,
) => {
	if (numMissingProfiles === 0) {
		return avatarData;
	}

	const name = intl.formatMessage(messages.MissingProfileName, {
		numMissingProfiles,
	});

	const label = intl.formatMessage(messages.MissingProfileTooltip, {
		name,
		numMissingProfiles,
	});

	return avatarData.concat([
		{
			name,
			label,
			href: '#', // Required otherwise tooltip doesn't render
			isDisabled: true,
		},
	]);
};

const AVATAR_LIMIT = 6;

interface AvatarGroupWrapperProps {
	allUserAvatarData: AvatarData[];
	avatarSize?: AvatarGroupProps['size'];
	onAvatarClick: (e: React.MouseEvent, index: number) => void;
}

const AvatarGroupWrapper = ({
	allUserAvatarData,
	avatarSize,
	onAvatarClick,
}: AvatarGroupWrapperProps) => (
	<Box xcss={styles.avatarGroupContainer}>
		<AvatarGroup
			appearance="stack"
			data={allUserAvatarData}
			maxCount={AVATAR_LIMIT}
			size={avatarSize}
			overrides={{
				Avatar: {
					// Adds tooltips on missing profile icon, ensures it shows up (by saying its not disabled)
					render: (Component, props, index) => {
						return props.isDisabled ? (
							<Tooltip content={props.label}>
								<Component
									{...props}
									key={props.name}
									isDisabled={false}
									href={undefined}
									onClick={(e) => onAvatarClick(e, index)}
								/>
							</Tooltip>
						) : (
							<Component {...props} href={undefined} onClick={(e) => onAvatarClick(e, index)} />
						);
					},
				},
			}}
		/>
	</Box>
);

interface OverflowAvatarStackProps {
	atlassianUsers: ReportingLinesUser[];
	numFilteredMissingProfiles: number;
	avatarSize?: AvatarGroupProps['size'];
	includeNameInMoreText?: boolean;
	onClick: () => void;
}

export const OverflowAvatarStack = ({
	atlassianUsers,
	numFilteredMissingProfiles,
	avatarSize,
	includeNameInMoreText = false,
	onClick: onStackClick,
}: OverflowAvatarStackProps): React.JSX.Element => {
	const intl = useIntl();
	const [, { selectUser }] = useReportingLinesStore();

	const avatarsToShow = atlassianUsers.slice(0, AVATAR_LIMIT);

	const atlassianUsersAvatarGroupData: AvatarData[] = avatarsToShow.map((user) => {
		const isNotMissingProfile = !!user.pii?.name;

		const name =
			user.pii?.name ??
			intl.formatMessage(messages.MissingProfileName, {
				numMissingProfiles: 1,
			});
		const jobTitle = user.pii?.extendedProfile?.jobTitle ?? undefined;
		const label = isNotMissingProfile
			? `${name}${jobTitle ? ` (${jobTitle})` : ''}`
			: intl.formatMessage(messages.MissingProfileTooltip, {
					name,
					numMissingProfiles: 1,
				});

		return {
			name,
			src: user.pii?.picture ?? undefined,
			href:
				(user.identifierType === AccountIdentifierType.ATLASSIAN_ID && user.accountIdentifier) ||
				undefined,
			label,
		};
	});

	// Append an avatar as a placeholder for missing profiles when there is space to show it
	const allUserAvatarData =
		atlassianUsersAvatarGroupData.length < AVATAR_LIMIT
			? appendMissingProfileStackItemData(
					atlassianUsersAvatarGroupData,
					numFilteredMissingProfiles,
					intl,
				)
			: atlassianUsersAvatarGroupData;

	const onAvatarClick = (e: React.MouseEvent, index: number) => {
		e.stopPropagation();
		e.preventDefault();

		const identifier = atlassianUsers[index]?.accountIdentifier;
		if (identifier) {
			selectUser(identifier);
		}
	};

	const numUsers = atlassianUsers.length + numFilteredMissingProfiles;

	return (
		<Inline space="space.050" alignBlock="center" alignInline="center">
			<AvatarGroupWrapper
				allUserAvatarData={allUserAvatarData}
				avatarSize={avatarSize}
				onAvatarClick={onAvatarClick}
			/>
			<Styled.OverflowStackCard
				onClick={onStackClick}
				onKeyDown={({ key }) => {
					if (key === 'Enter' || key === ' ') {
						onStackClick();
					}
				}}
			>
				<Styled.OverflowStackMoreText>
					{intl.formatMessage(
						includeNameInMoreText
							? messages.OverflowAvatarStackShowMore
							: messages.OverflowAvatarStackMore,
						{
							numUsers: numUsers,
						},
					)}
				</Styled.OverflowStackMoreText>
			</Styled.OverflowStackCard>
		</Inline>
	);
};
